import { AnimatePresence, motion } from 'motion/react';

import { Icons } from '@/assets/icons/icons';
import { Box, Grid, XStack, YStack } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { ScrollMask } from '@/components/ui/ScrollMask';
import { Skeleton } from '@/components/ui/skeleton';
import { FEATURES, useFeature } from '@/features/feature-flags';
import { getIntegrationIcon } from '@/integrations/integration-icons';
import { cn } from '@/lib/utils';

import {
  useFocusOnMount,
  useGoogleConsentOfferTracking,
  type useFeedOnboardingFlow,
  type useFeedOnboardingLayout,
} from './feed-onboarding.hooks';
import { FeedDemo } from './FeedDemo';

type FeedOnboardingFlow = ReturnType<typeof useFeedOnboardingFlow>;
type FeedOnboardingLayout = ReturnType<typeof useFeedOnboardingLayout>;
type OnboardingApp = FeedOnboardingFlow['moreApps'][number];

const PLACEHOLDER_ROW_COUNT = 3;
const APP_LIST_ENTER_DELAY = 0.1;
const MotionButton = motion.create(Button);
const AnimatedScrollMask = motion.create(ScrollMask);

const riseIn = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, transition: { duration: 0.12 } },
  transition: { duration: 0.3, ease: 'easeOut' as const },
};

const rowClass =
  'flex h-14 w-full shrink-0 items-center gap-3 rounded-xl border pl-3 pr-2.5 text-left';

const StepCopy = ({
  title,
  body,
  isSplit,
}: {
  title: string;
  body: string;
  isSplit: boolean;
}) => {
  const headingRef = useFocusOnMount<HTMLHeadingElement>();
  return (
    <motion.div
      {...riseIn}
      className={cn('flex flex-col gap-2', !isSplit && 'items-center')}
    >
      <h1
        ref={headingRef}
        tabIndex={-1}
        className="text-balance font-serif text-2xl leading-tight tracking-tight outline-none md:text-3xl"
      >
        {title}
      </h1>
      <p className="max-w-md text-pretty text-base leading-normal text-foreground-subtle">
        {body}
      </p>
    </motion.div>
  );
};

const AppIcon = ({ toolkitName }: { toolkitName: string }) => {
  const Icon = getIntegrationIcon(toolkitName);
  return (
    <XStack
      center
      className="size-9 shrink-0 rounded-lg border border-border bg-surface"
    >
      <Icon className="size-6" />
    </XStack>
  );
};

const AppRow = ({ app }: { app: OnboardingApp }) => (
  <XStack
    className={cn(
      rowClass,
      app.isConnected ? 'border-border' : 'border-border-strong bg-surface'
    )}
  >
    <AppIcon toolkitName={app.toolkitName} />
    <span className="min-w-0 flex-1 truncate text-base font-medium">
      {app.name}
    </span>
    {app.isConnected ? (
      <XStack className="shrink-0 items-center gap-1.5 pr-1.5 text-sm font-medium text-brand-dark-emerald-green dark:text-brand-emerald-green">
        <Icons.check className="size-4" />
        Connected
      </XStack>
    ) : (
      <Button
        variant="outline"
        size="sm"
        className="shrink-0 focus-ring"
        aria-label={`Connect ${app.name}`}
        isLoading={app.isConnecting}
        loadingText="Connecting…"
        onClick={app.connect}
      >
        Connect
      </Button>
    )}
  </XStack>
);

const AppRowPlaceholder = () => (
  <XStack className={cn(rowClass, 'border-border')}>
    <Skeleton className="size-9 shrink-0 rounded-lg" />
    <Skeleton className="h-3 w-28" />
    <Skeleton className="ml-auto h-8 w-16 rounded-lg" />
  </XStack>
);

const NoMoreApps = () => (
  <YStack className="items-center gap-1 rounded-xl border border-dashed border-border-strong px-5 py-6 text-center">
    <span className="text-base font-medium">
      No other apps to suggest right now
    </span>
    <span className="text-sm text-foreground-subtle">
      You can connect more any time from Integrations.
    </span>
  </YStack>
);

const AppList = ({
  apps,
  isLoading = false,
}: {
  apps: OnboardingApp[];
  isLoading?: boolean;
}) => {
  if (isLoading) {
    return (
      <YStack className="gap-2" aria-busy aria-label="Loading apps">
        {Array.from({ length: PLACEHOLDER_ROW_COUNT }, (_, index) => (
          <AppRowPlaceholder key={index} />
        ))}
      </YStack>
    );
  }
  if (apps.length === 0) return <NoMoreApps />;
  return (
    <>
      {apps.map((app) => (
        <AppRow key={app.toolkitName} app={app} />
      ))}
    </>
  );
};

const StepContent = ({
  flow,
  isSplit,
}: {
  flow: FeedOnboardingFlow;
  isSplit: boolean;
}) => {
  const isMorePhase = flow.step === 'connect-more';
  // Copy and list swap together: a list swapped ahead of the copy sat under
  // the old, taller heading and overflowed until it left, flashing a scrollbar
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={flow.step}
        exit={riseIn.exit}
        className="flex min-h-0 flex-col gap-6"
      >
        {isMorePhase ? (
          <StepCopy
            isSplit={isSplit}
            title="Connect more apps"
            body="The more you connect, the more Littlebird can line up for you."
          />
        ) : (
          <StepCopy
            isSplit={isSplit}
            title="Littlebird creates a single feed of everything that matters"
            body="Every app you connect adds items to your feed. Get started with Gmail and Calendar."
          />
        )}
        <AnimatedScrollMask
          {...riseIn}
          transition={{ ...riseIn.transition, delay: APP_LIST_ENTER_DELAY }}
          showEndFade
          className="-m-1 flex min-h-0 flex-col gap-2 overflow-x-hidden overflow-y-auto p-1"
        >
          {!isMorePhase && flow.googleConsent.canConnect ? (
            <GoogleConsentButton consent={flow.googleConsent} />
          ) : (
            <AppList
              apps={isMorePhase ? flow.moreApps : flow.googleApps}
              isLoading={
                isMorePhase
                  ? flow.isLoadingMoreApps
                  : flow.googleConsent.isLoading
              }
            />
          )}
        </AnimatedScrollMask>
      </motion.div>
    </AnimatePresence>
  );
};

const GoogleConsentButton = ({
  consent,
}: {
  consent: FeedOnboardingFlow['googleConsent'];
}) => {
  useGoogleConsentOfferTracking();
  return (
    <Button
      size="lg"
      className="w-full shrink-0 focus-ring"
      isLoading={consent.isConnecting}
      loadingText="Connecting…"
      onClick={consent.connect}
    >
      Connect Gmail and Google Calendar
    </Button>
  );
};

const StepAction = ({ flow }: { flow: FeedOnboardingFlow }) => {
  const checkFeature = useFeature();
  const isMorePhase = flow.step === 'connect-more';
  const requireConnection =
    !isMorePhase && checkFeature(FEATURES.WEB_ONBOARDING_REQUIRE_CONNECTION);
  const isPrimary = isMorePhase
    ? flow.hasConnectedApps
    : flow.hasConnectedGoogleApps || requireConnection;
  const needsConnection = requireConnection && !flow.hasConnectedGoogleApps;

  return (
    <YStack className="w-full gap-2">
      <AnimatePresence mode="wait">
        <MotionButton
          key={flow.step}
          initial={{ opacity: 0 }}
          animate={{ opacity: needsConnection ? 0.5 : 1 }}
          exit={riseIn.exit}
          transition={{
            ...riseIn.transition,
            delay: APP_LIST_ENTER_DELAY + riseIn.transition.duration,
          }}
          size="lg"
          textSize={isPrimary ? 'base' : 'sm'}
          variant={isPrimary ? 'default' : 'link'}
          className={cn(
            'focus-ring self-center md:self-start',
            isPrimary
              ? 'h-12 w-full px-8 md:w-auto md:min-w-40'
              : 'min-h-[44px] w-auto px-0 text-foreground-muted'
          )}
          disabled={needsConnection}
          aria-describedby={
            needsConnection ? 'onboarding-connection-requirement' : undefined
          }
          onClick={flow.goNext}
        >
          {isPrimary ? 'Continue' : "I'll do it later"}
        </MotionButton>
      </AnimatePresence>
      {needsConnection && (
        <p
          id="onboarding-connection-requirement"
          className="text-sm text-foreground-subtle"
        >
          Connect Gmail or Google Calendar to continue.
        </p>
      )}
    </YStack>
  );
};

export const ConnectStep = ({
  flow,
  layout,
}: {
  flow: FeedOnboardingFlow;
  layout: FeedOnboardingLayout;
}) => {
  const isMorePhase = flow.step === 'connect-more';

  if (layout.isSplit) {
    return (
      <Grid className="min-h-0 flex-1 grid-cols-2 grid-rows-1 items-center gap-8 lg:gap-12">
        <XStack className="justify-center lg:px-4">
          <Box className="w-full max-w-md">
            <FeedDemo size={layout.demoSize} />
          </Box>
        </XStack>
        <YStack className="max-h-full min-h-0 justify-center gap-6 py-2">
          <StepContent flow={flow} isSplit />
          <XStack>
            <StepAction flow={flow} />
          </XStack>
        </YStack>
      </Grid>
    );
  }

  return (
    <YStack className="min-h-0 flex-1 gap-4">
      <ScrollMask
        showEndFade
        className="-mx-6 flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto px-6"
      >
        <YStack className="mx-auto my-auto w-full max-w-md gap-6 py-4 text-center">
          <FeedDemo
            size={layout.demoSize}
            height={isMorePhase ? 0 : undefined}
          />
          <StepContent flow={flow} isSplit={false} />
        </YStack>
      </ScrollMask>
      <XStack className="justify-center">
        <StepAction flow={flow} />
      </XStack>
    </YStack>
  );
};
